import React, { ReactElement, ReactFragment, ReactPortal, useMemo } from 'react'
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  ListItem,
  ListItemText,
  MenuItem,
  List,
  Typography,
  IconButton,
  Chip,
  ListSubheader,
} from '@mui/material'
import Select from 'components/Select'
import { useFilters } from './FiltersContext'
import { useTranslation } from 'react-i18next'
import { useForm, type FieldValues } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import type { Filters, FiltersProviderProps, Heading, Option } from './FiltersContext.types'
import Input from 'components/Input'
import { useAppContext } from 'context/AppContext/AppContext'
import { format } from 'date-fns'

const FiltersList = ({ filterGroups }: Pick<FiltersProviderProps, 'filterGroups'>): JSX.Element => {
  const { markets } = useAppContext()
  const { t } = useTranslation()
  const { filters, toggleDrawer, isOpen, headings, options, customFilters, changeFilters, renderFilters } = useFilters()

  const localDatePickers = [...headings, ...customFilters].filter(el => el.name.toLowerCase().includes('local'))

  const defaultValues: (type: 'reset' | 'load') => Record<string, string | boolean | null | string[] | Date> = type =>
    [...headings, ...customFilters].reduce<Record<string, string | boolean | null | string[] | Date>>((a, b) => {
      switch (b.type) {
        case 'text': {
          return {
            ...a,
            [b.field]: type === 'reset' || !filters[b.field] ? '' : filters[b.field],
          }
        }
        case 'date-range':
        case 'datetime-range': {
          return {
            ...a,
            [`${b.field}Start`]:
              type === 'reset' || !filters[`${b.field}Start`] ? null : new Date(filters[`${b.field}Start`] as string),
            [`${b.field}Finish`]:
              type === 'reset' || !filters[`${b.field}Finish`] ? null : new Date(filters[`${b.field}Finish`] as string),
          }
        }
        case 'options': {
          return {
            ...a,
            [b.field]: type === 'reset' || !filters[b.field] ? '' : filters[b.field],
          }
        }
        case 'multipicker': {
          return {
            ...a,
            [b.field]: type === 'reset' || !filters[b.field] ? [] : filters[b.field],
          }
        }
        case 'boolean': {
          return {
            ...a,
            [b.field]: type === 'reset' || !filters[b.field] ? false : String(filters[b.field]) === 'true',
          }
        }
        default: {
          return { ...a }
        }
      }
    }, {})

  const validationObject = yup.object(
    headings.reduce<yup.ObjectShape>((a, b) => {
      if (b.type === 'date-range' || b.type === 'datetime-range') {
        return {
          ...a,
          [`${b.field}Start`]: yup.date().nullable(),
          [`${b.field}Finish`]: yup
            .date()
            .nullable()
            .when(`${b.field}Start`, ([enteredDate], schema) => {
              if (enteredDate !== null) {
                return schema.min(
                  enteredDate,
                  t('forms.finishLaterThanStart') ?? 'The end date must be later than the start date'
                )
              }

              return schema
            }),
        }
      }
      return { ...a }
    }, {})
  )

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    values: defaultValues('load'),
    resolver: yupResolver(validationObject),
  })
  const isMarketSelected = watch('marketId')

  const handleSaveFilters = (data: any): void => {
    const newFilters = Object.fromEntries(
      Object.entries({
        ...data,
        page: 1,
      }).filter(
        ([key, value]) =>
          value !== '' && value !== null && value !== undefined && !(value instanceof Array && value.length === 0)
      )
    ) as Filters
    changeFilters(newFilters)
  }

  const handleRemoveFilter = (fieldName: string) => {
    const newFilters = Object.fromEntries(Object.entries(filters).filter(([fieldKey]) => fieldKey !== fieldName))
    changeFilters(newFilters)
  }

  // const clearFilters = (): void => {
  //   changeFilters({})
  //   // changeExportMode?.(false)
  // }

  const userFilters = useMemo(() => {
    return Object.entries(filters).filter(
      ([key, value]) =>
        !(
          ['order', 'orderField', 'take', 'page', 'tab'].includes(key) ||
          (key === 'status' && value instanceof Array && value.length === 0)
        )
    )
  }, [filters])

  const renderFiltersList = (data: Heading[]) =>
    data
      .sort((a, b) => (a.order !== undefined && b.order !== undefined ? a.order - b.order : -1))
      .map(({ field, name, type, hide }) => {
        if (hide) return null
        if (field === '') {
          return null
        }
        switch (type) {
          case 'options': {
            const optionsToSelect = options[field]?.filter(e => e.value) ?? []
            return (
              <ListItem key={field} sx={{ flexDirection: 'column', mx: 0, px: 0, alignItems: 'flex-start' }}>
                <Typography mb={0.5} ml={0.25} display="block" fontWeight={500} variant="body2">
                  {name}
                </Typography>
                <Box display="flex" width="100%">
                  <Select
                    control={control}
                    elements={[...optionsToSelect]}
                    size="small"
                    label=""
                    name={field}
                    MenuProps={{
                      PaperProps: { style: { boxShadow: '0 0 8px #777' } },
                    }}
                    renderFunction={(option: Option, idx) => (
                      <MenuItem key={idx} value={option.id}>
                        {option.value}
                      </MenuItem>
                    )}
                  />
                </Box>
              </ListItem>
            )
          }
          case 'multipicker': {
            const optionsToShow = [...(options[field]?.filter(e => e.value) ?? [])]
            const renderValues = (ids: string[]) => (
              <Box display="flex" gap={1} overflow="clip">
                {optionsToShow
                  .filter(option => ids.includes(option.id))
                  .map(e => (typeof e.value === 'string' ? <span key={e.value}>{e.value}</span> : e.value))}
              </Box>
            )

            const isSomeSelected = optionsToShow
              .map(el => el.id)
              .some(id => (watch(field) as unknown as string[])?.includes(id))

            return (
              <ListItem key={field} sx={{ flexDirection: 'column', mx: 0, px: 0, alignItems: 'flex-start' }}>
                <Typography mb={0.5} ml={0.25} display="block" fontWeight={500} variant="body2">
                  {name}
                </Typography>
                <Box display="flex" width="100%" gap={1}>
                  <Select<Option, FieldValues>
                    control={control}
                    elements={optionsToShow}
                    label=""
                    multiple
                    size="small"
                    MenuProps={{
                      PaperProps: { style: { boxShadow: '0 0 8px #777' } },
                    }}
                    name={field}
                    // @ts-expect-error-next-line
                    renderValue={renderValues}
                    renderFunction={(option, idx) => (
                      <MenuItem key={idx} value={option.id}>
                        <Checkbox checked={(getValues(field) as string[])?.includes(option.id)} sx={{ p: 0.5 }} />
                        <ListItemText sx={{ fontSize: 16 }} primary={option.value} />
                      </MenuItem>
                    )}
                  />
                  <Button
                    sx={{ width: 120 }}
                    onClick={() => {
                      setValue(field, isSomeSelected ? [] : optionsToShow.map(el => el.id))
                    }}
                  >
                    {isSomeSelected ? 'Unselect all' : 'All'}
                  </Button>
                </Box>
              </ListItem>
            )
          }
          case 'text': {
            return (
              <ListItem
                key={field}
                sx={{ mx: 0, px: 0, flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}
              >
                <Typography mb={0.5} ml={0.25} display="block" fontWeight={500} variant="body2">
                  {name}
                </Typography>
                <Box display="flex" width="100%">
                  <Input
                    control={control}
                    label=""
                    name={field}
                    textInputProps={{
                      size: 'small',
                      sx: { mx: 0 },
                      placeholder: t(`filters.placeholders.${field}`).toString(),
                    }}
                  />
                </Box>
              </ListItem>
            )
          }
          case 'date-range':
          case 'datetime-range': {
            const isLocal = field.toLowerCase().includes('local')
            const hasLocalMatch = !isLocal && localDatePickers.some(el => el.type.includes(type))

            const content = (
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} width="100%">
                <Input
                  wrapperProps={{ flexBasis: '250px', flexGrow: 1 }}
                  type={type.replace('-range', '') as 'date' | 'datetime'}
                  name={`${field}Start`}
                  label={`${name} Start`}
                  control={control}
                  dateInputProps={{
                    slotProps: {
                      textField: {
                        size: 'small',
                        sx: { mx: 0, backgroundColor: 'red' },
                        fullWidth: true,
                      },
                    },
                  }}
                  datetimeInputProps={{
                    slotProps: {
                      textField: {
                        size: 'small',
                        sx: { mx: 0, backgroundColor: 'red' },
                        fullWidth: true,
                      },
                    },
                  }}
                />
                -
                <Input
                  wrapperProps={{ flexBasis: '250px', flexGrow: 1 }}
                  type={type.replace('-range', '') as 'date' | 'datetime'}
                  name={`${field}Finish`}
                  label={`${name} Finish`}
                  control={control}
                  dateInputProps={{
                    slotProps: {
                      textField: {
                        size: 'small',
                        sx: { mx: 0 },
                        fullWidth: true,
                      },
                    },
                  }}
                  datetimeInputProps={{
                    slotProps: {
                      textField: {
                        size: 'small',
                        sx: { mx: 0 },
                        fullWidth: true,
                      },
                    },
                  }}
                />
              </Box>
            )

            if (isLocal && isMarketSelected) {
              return (
                <ListItem key={field} sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0 }}>
                  <Typography mb={0.75} ml={0.25} display="block" fontWeight={500} variant="body2">
                    {name} time
                  </Typography>
                  {content}
                </ListItem>
              )
            }

            if (isLocal) {
              if (markets != null && markets.data.length > 1) {
                return null
              } else {
                return (
                  <ListItem key={field} sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0 }}>
                    <Typography mb={0.75} ml={0.25} display="block" fontWeight={500} variant="body2">
                      {name} time
                    </Typography>
                    {content}
                  </ListItem>
                )
              }
            } else {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              if ((!isMarketSelected && markets != null && markets.data.length > 1) || !hasLocalMatch) {
                return (
                  <ListItem key={field} sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 0 }}>
                    <Typography mb={0.75} ml={0.25} display="block" fontWeight={500} variant="body2">
                      Server timezone ({process.env.REACT_APP_API_TIMEZONE})
                    </Typography>
                    {content}
                  </ListItem>
                )
              } else {
                return null
              }
            }
          }
          case 'boolean':
            return (
              <ListItem key={field} sx={{ display: 'block', px: 0 }}>
                <Input type="switch" name={field} label={name} control={control} />
              </ListItem>
            )
          default: {
            return null
          }
        }
      })

  return (
    <div>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {userFilters.map(([fieldKey, fieldValue]) => {
          let labelValue: string | ReactElement | ReactFragment | ReactPortal = ''
          const renderExist = renderFilters?.find(el => el.field === fieldKey)?.render

          if (renderExist) {
            const value = renderFilters
              ?.find(el => el.field === fieldKey)
              ?.render(fieldValue, () => {
                handleRemoveFilter(fieldKey)
              })
            if (value instanceof Object) {
              labelValue = value
            } else if (value === null) {
              return null
            } else {
              labelValue = `${value}`
            }
          } else {
            if (
              typeof fieldValue === 'string' &&
              new Date(fieldValue).toString() !== 'Invalid Date' &&
              (fieldKey.endsWith('Finish') || fieldKey.endsWith('Start'))
            ) {
              const isDate = new Date(fieldValue)

              labelValue = `${format(isDate, 'dd/MM/yyyy, HH:mm')}`
            }
          }

          return (
            <Chip
              sx={{ borderRadius: 2, backgroundColor: 'background.paper', borderColor: 'grey.300' }}
              onDelete={() => {
                handleRemoveFilter(fieldKey)
              }}
              size="medium"
              variant="outlined"
              key={`filter-${fieldKey}-${JSON.stringify(fieldValue)}`}
              label={
                typeof labelValue === 'string'
                  ? `${fieldKey} ${fieldKey.endsWith('Start') ? 'from' : fieldKey.endsWith('Finish') ? 'to' : 'equal'} "${labelValue || fieldValue}"`
                  : labelValue
              }
            />
          )
        })}
        <Badge badgeContent={userFilters.length} color="error">
          <Button
            sx={{ mb: 1, backgroundColor: 'background.paper', borderColor: 'grey.300' }}
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => {
              toggleDrawer()
            }}
            startIcon={<FilterListOutlinedIcon />}
          >
            {userFilters?.length ? t('common.moreFilters') : t('common.filter')}
          </Button>
        </Badge>
      </Box>
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            bottom: 14,
            top: 14,
            right: 14,
            height: 'auto',
            borderRadius: 2,
            backgroundColor: 'grey.200',
            backgroundImage: 'none',
          },
        }}
        onClose={() => {
          toggleDrawer(false)
          // changeExportMode?.(false)
        }}
      >
        <Box display="flex" justifyContent="space-between" p={2}>
          <Typography variant="h5" fontWeight={600}>
            Apply filter
          </Typography>
          <IconButton
            onClick={() => {
              toggleDrawer(false)
              // changeExportMode?.(false)
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
        <Box
          m={2}
          mt={0}
          p={2}
          height="100%"
          sx={{ backgroundColor: 'grey.50', border: 'border: 1px solid #EAEDF2', borderRadius: 2 }}
        >
          <form
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            onSubmit={handleSubmit(handleSaveFilters)}
          >
            <List sx={{ maxWidth: '100vw', width: 320, '@media (min-width: 768px)': { width: 600 } }}>
              {renderFiltersList([...headings, ...customFilters].filter(el => el.group === undefined))}
              {filterGroups?.map((item, idx) => (
                <React.Fragment key={`filter-group-${item.id}`}>
                  <ListSubheader
                    sx={{
                      backgroundColor: 'transparent',
                      px: 0,
                      mt: !!idx ? 2 : 0,
                      pb: 2,
                      mb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    <Typography fontWeight={500} fontSize={18} color="grey.900">
                      {item.label}
                    </Typography>
                  </ListSubheader>
                  {renderFiltersList([...headings, ...customFilters].filter(el => el.group === item.id))}
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ display: 'flex', mt: 'auto', flexDirection: 'column', gap: 1 }}>
              {!isMarketSelected && localDatePickers.length > 0 && markets != null && markets.data?.length > 1 && (
                <Typography fontWeight="700" color="error.dark" fontSize="12px">
                  When downloading the database, select a single market if you want a detailed data.
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    toggleDrawer(false)
                    // changeExportMode?.(false)
                  }}
                >
                  {t('common.cancel')}
                </Button>
                {/* <Button fullWidth variant="outlined" color="warning" onClick={clearFilters}>
                  {t('common.clearFilters')}
                </Button> */}
                <Button fullWidth variant="contained" color="primary" type="submit">
                  {/* {isExportMode ? (exportText ?? t('common.export')) : t('common.saveFilters')} */}
                  {t('common.filter')}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Drawer>
    </div>
  )
}

export default FiltersList
